/**
 * SHARED
 * Cross-cutting code usable by any module. Must not import from any module.
 *
 * referenceDataService :: per-tenant master/reference data over RLS.
 *
 * Product SKUs (products + product_sizes / product_flavors / pack_sizes) and
 * sales geography (sales_states) are CROSS-MODULE reference data: the customer
 * sets them up once here, and multiple modules read them (sales-forecasting
 * today; distribution map / orders / POS later). The data lives in
 * account-scoped tables (migration 0021) whose RLS pins every row to the
 * caller's workspace via is_account_member(account_id). We talk to the tables
 * directly with the authenticated client — same model as accountService /
 * forecastService.
 *
 * Writes inject the caller's account_id (so the RLS WITH CHECK passes) and only
 * send whitelisted columns; ids / created_at are DB-controlled. Errors are
 * logged server-side detail and returned generic to the caller.
 */

import { supabase } from "./supabaseClient.js";
import { getMyPrimaryAccount } from "./accountService.js";
import { isValidCountyFips, isoToStateFips } from "./countyGeo.js";
import { fetchAllRows } from "./fetchAllRows.js";

// ── Active account (the tenant whose reference data we manage) ───────────────
let _accountIdPromise = null;

export function resetReferenceAccount() {
  _accountIdPromise = null;
}

async function activeAccountId() {
  if (_accountIdPromise) return _accountIdPromise;
  _accountIdPromise = (async () => {
    const acct = await getMyPrimaryAccount();
    if (!acct?.accountId) throw new Error("No workspace found for your account.");
    return acct.accountId;
  })();
  return _accountIdPromise;
}

// A write that violates RLS (wrong/again account) or an FK still in use.
function writeError(where, error) {
  console.error(`[referenceDataService.${where}]`, error);
  if (error?.code === "42501" || /row-level security/i.test(error?.message ?? "")) {
    return new Error("Only members of your workspace can change these settings.");
  }
  if (error?.code === "23503") {
    return new Error("That item is still in use and can't be deleted yet.");
  }
  if (error?.code === "23505") {
    return new Error("That entry already exists.");
  }
  return new Error("Could not save your changes.");
}

function trimName(value, label = "Name") {
  const n = String(value ?? "").trim();
  if (!n) throw new Error(`${label} is required.`);
  if (n.length > 120) throw new Error(`${label} must be 120 characters or fewer.`);
  return n;
}

// =====================================================================
// Sizes / Flavors / Pack sizes — the building blocks a SKU is assembled from.
// =====================================================================

export async function listSizes() {
  const acct = await activeAccountId();
  const { data, error } = await supabase
    .from("product_sizes")
    .select("id, name, abbreviation, units_per_case, sort_order")
    .eq("account_id", acct)
    .order("sort_order", { ascending: true })
    .order("name", { ascending: true });
  if (error) { console.error("[referenceDataService.listSizes]", error); throw new Error("Could not load sizes."); }
  return data ?? [];
}

export async function createSize({ name, abbreviation, unitsPerCase, sortOrder } = {}) {
  const acct = await activeAccountId();
  const { data, error } = await supabase
    .from("product_sizes")
    .insert({
      account_id: acct,
      name: trimName(name, "Size name"),
      abbreviation: abbreviation ? String(abbreviation).trim().slice(0, 24) : null,
      units_per_case: unitsPerCase != null && unitsPerCase !== "" ? Number(unitsPerCase) : null,
      sort_order: Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 0,
    })
    .select("id, name, abbreviation, units_per_case, sort_order")
    .single();
  if (error) throw writeError("createSize", error);
  return data;
}

export async function updateSize(id, { name, abbreviation, unitsPerCase, sortOrder } = {}) {
  const patch = {};
  if (name !== undefined) patch.name = trimName(name, "Size name");
  if (abbreviation !== undefined) patch.abbreviation = abbreviation ? String(abbreviation).trim().slice(0, 24) : null;
  if (unitsPerCase !== undefined) patch.units_per_case = unitsPerCase != null && unitsPerCase !== "" ? Number(unitsPerCase) : null;
  if (sortOrder !== undefined) patch.sort_order = Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 0;
  const { error } = await supabase.from("product_sizes").update(patch).eq("id", id);
  if (error) throw writeError("updateSize", error);
  return { id };
}

export async function deleteSize(id) {
  const { error } = await supabase.from("product_sizes").delete().eq("id", id);
  if (error) throw writeError("deleteSize", error);
  return { id };
}

export async function listFlavors() {
  const acct = await activeAccountId();
  const { data, error } = await supabase
    .from("product_flavors")
    .select("id, name, status, sort_order")
    .eq("account_id", acct)
    .order("sort_order", { ascending: true })
    .order("name", { ascending: true });
  if (error) { console.error("[referenceDataService.listFlavors]", error); throw new Error("Could not load flavors."); }
  return data ?? [];
}

export async function createFlavor({ name, status, sortOrder } = {}) {
  const acct = await activeAccountId();
  const { data, error } = await supabase
    .from("product_flavors")
    .insert({
      account_id: acct,
      name: trimName(name, "Flavor name"),
      status: status ? String(status).trim().slice(0, 40) : null,
      sort_order: Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 0,
    })
    .select("id, name, status, sort_order")
    .single();
  if (error) throw writeError("createFlavor", error);
  return data;
}

export async function updateFlavor(id, { name, status, sortOrder } = {}) {
  const patch = {};
  if (name !== undefined) patch.name = trimName(name, "Flavor name");
  if (status !== undefined) patch.status = status ? String(status).trim().slice(0, 40) : null;
  if (sortOrder !== undefined) patch.sort_order = Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 0;
  const { error } = await supabase.from("product_flavors").update(patch).eq("id", id);
  if (error) throw writeError("updateFlavor", error);
  return { id };
}

export async function deleteFlavor(id) {
  const { error } = await supabase.from("product_flavors").delete().eq("id", id);
  if (error) throw writeError("deleteFlavor", error);
  return { id };
}

export async function listPackSizes() {
  const acct = await activeAccountId();
  const { data, error } = await supabase
    .from("pack_sizes")
    .select("id, name, sort_order")
    .eq("account_id", acct)
    .order("sort_order", { ascending: true })
    .order("name", { ascending: true });
  if (error) { console.error("[referenceDataService.listPackSizes]", error); throw new Error("Could not load pack sizes."); }
  return data ?? [];
}

export async function createPackSize({ name, sortOrder } = {}) {
  const acct = await activeAccountId();
  const { data, error } = await supabase
    .from("pack_sizes")
    .insert({
      account_id: acct,
      name: trimName(name, "Pack size name"),
      sort_order: Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 0,
    })
    .select("id, name, sort_order")
    .single();
  if (error) throw writeError("createPackSize", error);
  return data;
}

export async function updatePackSize(id, { name, sortOrder } = {}) {
  const patch = {};
  if (name !== undefined) patch.name = trimName(name, "Pack size name");
  if (sortOrder !== undefined) patch.sort_order = Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 0;
  const { error } = await supabase.from("pack_sizes").update(patch).eq("id", id);
  if (error) throw writeError("updatePackSize", error);
  return { id };
}

export async function deletePackSize(id) {
  const { error } = await supabase.from("pack_sizes").delete().eq("id", id);
  if (error) throw writeError("deletePackSize", error);
  return { id };
}

// =====================================================================
// Products (SKUs)
// =====================================================================

/**
 * Returns: [{ id, name, active, tag:[...], size_id, flavor_id, pack_size_id,
 *             size:{name,abbreviation}, flavor:{name}, pack_size:{name} }]
 */
export async function listProducts() {
  const acct = await activeAccountId();
  const { data, error } = await supabase
    .from("products")
    .select(
      "id, name, active, tag, size_id, flavor_id, pack_size_id, " +
        "size:product_sizes(name, abbreviation), flavor:product_flavors(name), pack_size:pack_sizes(name)"
    )
    .eq("account_id", acct)
    .order("name", { ascending: true });
  if (error) { console.error("[referenceDataService.listProducts]", error); throw new Error("Could not load products."); }
  return data ?? [];
}

function normalizeTag(tag) {
  if (Array.isArray(tag)) return tag.map((t) => String(t).trim()).filter(Boolean).slice(0, 20);
  if (tag == null || tag === "") return [];
  return String(tag).split(",").map((t) => t.trim()).filter(Boolean).slice(0, 20);
}

export async function createProduct({ name, sizeId, flavorId, packSizeId, tag, active } = {}) {
  const acct = await activeAccountId();
  const { data, error } = await supabase
    .from("products")
    .insert({
      account_id: acct,
      name: trimName(name, "Product name"),
      size_id: sizeId || null,
      flavor_id: flavorId || null,
      pack_size_id: packSizeId || null,
      tag: normalizeTag(tag),
      active: active === undefined ? true : !!active,
    })
    .select("id")
    .single();
  if (error) throw writeError("createProduct", error);
  return data;
}

export async function updateProduct(id, { name, sizeId, flavorId, packSizeId, tag, active } = {}) {
  const patch = {};
  if (name !== undefined) patch.name = trimName(name, "Product name");
  if (sizeId !== undefined) patch.size_id = sizeId || null;
  if (flavorId !== undefined) patch.flavor_id = flavorId || null;
  if (packSizeId !== undefined) patch.pack_size_id = packSizeId || null;
  if (tag !== undefined) patch.tag = normalizeTag(tag);
  if (active !== undefined) patch.active = !!active;
  const { error } = await supabase.from("products").update(patch).eq("id", id);
  if (error) throw writeError("updateProduct", error);
  return { id };
}

export async function deleteProduct(id) {
  const { error } = await supabase.from("products").delete().eq("id", id);
  if (error) throw writeError("deleteProduct", error);
  return { id };
}

// =====================================================================
// Sales states (state -> region -> distributor)
// =====================================================================

export async function listSalesStates() {
  const acct = await activeAccountId();
  const { data, error } = await supabase
    .from("sales_states")
    .select("id, state_name, region, distributor, active")
    .eq("account_id", acct)
    .order("state_name", { ascending: true });
  if (error) { console.error("[referenceDataService.listSalesStates]", error); throw new Error("Could not load states."); }
  return data ?? [];
}

export async function createSalesState({ stateName, region, distributor, active } = {}) {
  const acct = await activeAccountId();
  const { data, error } = await supabase
    .from("sales_states")
    .insert({
      account_id: acct,
      state_name: trimName(stateName, "State"),
      region: region ? String(region).trim().slice(0, 120) : null,
      distributor: distributor ? String(distributor).trim().slice(0, 160) : null,
      active: active === undefined ? true : !!active,
    })
    .select("id, state_name, region, distributor, active")
    .single();
  if (error) throw writeError("createSalesState", error);
  return data;
}

export async function updateSalesState(id, { stateName, region, distributor, active } = {}) {
  const patch = {};
  if (stateName !== undefined) patch.state_name = trimName(stateName, "State");
  if (region !== undefined) patch.region = region ? String(region).trim().slice(0, 120) : null;
  if (distributor !== undefined) patch.distributor = distributor ? String(distributor).trim().slice(0, 160) : null;
  if (active !== undefined) patch.active = !!active;
  const { error } = await supabase.from("sales_states").update(patch).eq("id", id);
  if (error) throw writeError("updateSalesState", error);
  return { id };
}

export async function deleteSalesState(id) {
  const { error } = await supabase.from("sales_states").delete().eq("id", id);
  if (error) throw writeError("deleteSalesState", error);
  return { id };
}

// =====================================================================
// Territory managers — one Team user per territory (= sales_states.region).
// Stored in `regional_sales_coverage` (account_id, region) -> manager_user_id.
// Reads are account-scoped (RLS); the manager must be a workspace member.
// Territories themselves come from the distinct regions on sales_states.
// =====================================================================

/** [{ region, manager_user_id }] for the account (only regions with a row). */
export async function listTerritoryManagers() {
  const acct = await activeAccountId();
  const { data, error } = await supabase
    .from("regional_sales_coverage")
    .select("region, manager_user_id")
    .eq("account_id", acct);
  if (error) { console.error("[referenceDataService.listTerritoryManagers]", error); throw new Error("Could not load territory managers."); }
  return data ?? [];
}

/** Assign (or clear, when userId is empty) the manager for one territory/region. */
export async function setTerritoryManager(region, userId) {
  const acct = await activeAccountId();
  const r = String(region || "").trim();
  if (!r) throw new Error("Territory is required.");
  if (!userId) {
    const { error } = await supabase
      .from("regional_sales_coverage")
      .update({ manager_user_id: null })
      .eq("account_id", acct).eq("region", r);
    if (error) throw writeError("setTerritoryManager", error);
    return { region: r, manager_user_id: null };
  }
  const { error } = await supabase
    .from("regional_sales_coverage")
    .upsert({ account_id: acct, region: r, manager_user_id: userId }, { onConflict: "account_id,region" });
  if (error) throw writeError("setTerritoryManager", error);
  return { region: r, manager_user_id: userId };
}

// =====================================================================
// County-level distribution (System Settings → opt-in per state)
//
// Two account-scoped tables (migration 0037):
//   sales_state_modes  - per-state 'state' | 'county' mode (ISO state_code)
//   sales_counties     - one distributor per county (5-digit FIPS)
// The distribution map reads these LIVE (map-public) for distributor maps.
// =====================================================================

function trimDistributor(value) {
  const v = value == null ? "" : String(value).trim();
  if (v.length > 160) throw new Error("Distributor must be 160 characters or fewer.");
  return v;
}

// ── Per-state mode ───────────────────────────────────────────────────────────
export async function listSalesStateModes() {
  const acct = await activeAccountId();
  const { data, error } = await supabase
    .from("sales_state_modes")
    .select("state_code, mode")
    .eq("account_id", acct);
  if (error) { console.error("[referenceDataService.listSalesStateModes]", error); throw new Error("Could not load state modes."); }
  return data ?? [];
}

export async function setSalesStateMode(stateCode, mode) {
  if (mode !== "state" && mode !== "county") throw new Error("Invalid state mode.");
  if (!(await isoToStateFips(stateCode))) throw new Error(`Unknown state code: ${stateCode}`);
  const acct = await activeAccountId();
  const { error } = await supabase
    .from("sales_state_modes")
    .upsert({ account_id: acct, state_code: stateCode, mode }, { onConflict: "account_id,state_code" });
  if (error) throw writeError("setSalesStateMode", error);
  return { state_code: stateCode, mode };
}

// ── County → distributor assignments ─────────────────────────────────────────
export async function listSalesCounties() {
  const acct = await activeAccountId();
  // There are ~3,143 US counties and coverage is designed to span states, so
  // this is one of the few reads that reliably exceeds PostgREST's 1000-row
  // cap. Unpaginated, the counties past 1000 render as unassigned.
  try {
    return await fetchAllRows(
      () => supabase
        .from("sales_counties")
        .select("id, county_fips, distributor, active")
        .eq("account_id", acct)
        .order("county_fips", { ascending: true }),
      { label: "sales_counties" }
    );
  } catch (error) {
    console.error("[referenceDataService.listSalesCounties]", error);
    throw new Error("Could not load counties.");
  }
}

// Counties for one state, by 2-digit state FIPS prefix ('06' -> '06%').
export async function listSalesCountiesForState(stateFips) {
  const fips = String(stateFips);
  if (!/^[0-9]{2}$/.test(fips)) throw new Error(`Bad state FIPS: ${stateFips}`);
  const acct = await activeAccountId();
  const { data, error } = await supabase
    .from("sales_counties")
    .select("id, county_fips, distributor, active")
    .eq("account_id", acct)
    .like("county_fips", `${fips}%`);
  if (error) { console.error("[referenceDataService.listSalesCountiesForState]", error); throw new Error("Could not load counties."); }
  return data ?? [];
}

// Assign a distributor to a county, or clear it when distributor is empty.
export async function setCountyDistributor(countyFips, distributor) {
  if (!(await isValidCountyFips(countyFips))) throw new Error(`Unknown county: ${countyFips}`);
  const acct = await activeAccountId();
  const name = trimDistributor(distributor);
  if (!name) {
    const { error } = await supabase
      .from("sales_counties")
      .delete()
      .eq("account_id", acct)
      .eq("county_fips", String(countyFips));
    if (error) throw writeError("setCountyDistributor", error);
    return { county_fips: String(countyFips), distributor: null };
  }
  const { error } = await supabase
    .from("sales_counties")
    .upsert(
      { account_id: acct, county_fips: String(countyFips), distributor: name, active: true },
      { onConflict: "account_id,county_fips" }
    );
  if (error) throw writeError("setCountyDistributor", error);
  return { county_fips: String(countyFips), distributor: name };
}

// Bulk assign/clear (paint, or "set all to …"). Validates every FIPS first.
const MAX_BULK_COUNTIES = 400; // TX has 254 — one "set all" must fit in a call.
export async function setCountyDistributorBulk(countyFipsList, distributor) {
  if (!Array.isArray(countyFipsList) || countyFipsList.length === 0) return { count: 0 };
  if (countyFipsList.length > MAX_BULK_COUNTIES) {
    throw new Error(`Too many counties in one call (max ${MAX_BULK_COUNTIES}).`);
  }
  for (const f of countyFipsList) {
    if (!(await isValidCountyFips(f))) throw new Error(`Unknown county: ${f}`);
  }
  const acct = await activeAccountId();
  const name = trimDistributor(distributor);
  const fips = countyFipsList.map((f) => String(f));
  if (!name) {
    const { error } = await supabase
      .from("sales_counties")
      .delete()
      .eq("account_id", acct)
      .in("county_fips", fips);
    if (error) throw writeError("setCountyDistributorBulk", error);
    return { count: fips.length };
  }
  const rows = fips.map((f) => ({ account_id: acct, county_fips: f, distributor: name, active: true }));
  const { error } = await supabase
    .from("sales_counties")
    .upsert(rows, { onConflict: "account_id,county_fips" });
  if (error) throw writeError("setCountyDistributorBulk", error);
  return { count: rows.length };
}

// =====================================================================
// POS & Sample catalogs — per-tenant reference data set up in System Settings
// (Point of Sale Setup / Product Sample Orders Setup) and consumed by the POS &
// Fulfillment module. Tables are account-scoped (RLS via is_account_member).
// Moved here from pos-fulfillment/posService so the managing module
// (system-settings) and the consuming module (pos-fulfillment) share one source
// of truth through /shared. Writes whitelist columns; ids/account_id/timestamps
// are DB-controlled.
// =====================================================================

// Map only known keys from `src` (any-case) through `map` (srcKey -> column);
// coerce empties via `coerce`. Unknown keys (id, account_id, …) are dropped.
function pick(src, map, coerce = {}) {
  const out = {};
  for (const [k, col] of Object.entries(map)) {
    if (src[k] === undefined) continue;
    const fn = coerce[col];
    out[col] = fn ? fn(src[k]) : src[k];
  }
  return out;
}
const numOrNull = (v) => (v === "" || v == null ? null : Number(v));
const intOrNull = (v) => (v === "" || v == null ? null : parseInt(v, 10) || null);

// ── POS Items catalog ────────────────────────────────────────────────────────
// item_key from a name: "Door Cling 4x6" -> "doorCling4x6"
function itemKeyFromName(name) {
  const words = String(name || "").trim().split(/\s+/);
  return words
    .map((w, i) => (i === 0 ? w.charAt(0).toLowerCase() + w.slice(1) : w.charAt(0).toUpperCase() + w.slice(1)))
    .join("")
    .replace(/[^A-Za-z0-9]/g, "");
}

const POS_ITEM_COL_MAP = {
  name: "name",
  description: "description",
  sortOrder: "sort_order",
  currentInventory: "current_inventory",
  minOrderQty: "min_order_quantity",
  pricePerUnit: "price_per_unit",
  shippingCost: "shipping_cost",
  taxPerUnit: "tax_per_unit",
};
const POS_ITEM_COERCE = {
  sort_order: (v) => (Number.isFinite(Number(v)) ? Number(v) : 0),
  current_inventory: intOrNull,
  min_order_quantity: intOrNull,
  price_per_unit: numOrNull,
  shipping_cost: numOrNull,
  tax_per_unit: numOrNull,
};

export async function loadPOSItems() {
  const acct = await activeAccountId();
  const { data, error } = await supabase
    .from("pos_items")
    .select("*")
    .eq("account_id", acct)
    .order("sort_order", { ascending: true })
    .order("name", { ascending: true });
  if (error) { console.error("[referenceDataService.loadPOSItems]", error); throw new Error("Could not load POS items."); }
  return data ?? [];
}

export async function createPOSItem(fields) {
  const acct = await activeAccountId();
  const name = String(fields?.name ?? "").trim();
  if (!name) throw new Error("Item name is required.");
  const row = {
    account_id: acct,
    item_key: itemKeyFromName(name),
    active: true,
    ...pick(fields, POS_ITEM_COL_MAP, POS_ITEM_COERCE),
  };
  const { data, error } = await supabase.from("pos_items").insert(row).select("*").single();
  if (error) throw writeError("createPOSItem", error);
  return data;
}

export async function updatePOSItem(id, fields) {
  const acct = await activeAccountId();
  const patch = pick(fields, POS_ITEM_COL_MAP, POS_ITEM_COERCE);
  const { error } = await supabase.from("pos_items").update(patch).eq("account_id", acct).eq("id", id);
  if (error) throw writeError("updatePOSItem", error);
  return { id };
}

export async function updatePOSItemImageUrl(id, imageUrl) {
  const acct = await activeAccountId();
  const { error } = await supabase.from("pos_items").update({ image_url: imageUrl || null }).eq("account_id", acct).eq("id", id);
  if (error) throw writeError("updatePOSItemImageUrl", error);
  return { id };
}

export async function deletePOSItem(id) {
  const acct = await activeAccountId();
  const { error } = await supabase.from("pos_items").delete().eq("account_id", acct).eq("id", id);
  if (error) throw writeError("deletePOSItem", error);
  return { id };
}

// ── POS item images (private bucket, per-tenant) ─────────────────────────────
// `pos-item-images` is PRIVATE as of migration 0069 (backlog item 55): it was
// public-read with no tenant predicate, so every account's item photos were
// world-readable by URL. Reads are now gated by `is_account_member` on the
// account-uuid path prefix — the same audience that can see the `pos_items` row.
//
// Consequence for callers: there is no durable public URL any more.
// `pos_items.image_url` stores the STORAGE PATH (`<account_id>/<item_key>.<ext>`)
// and the browser gets a short-lived signed URL at render time. The column name
// is legacy; treat its contents as a path, not a URL.

const POS_IMAGE_BUCKET = "pos-item-images";
const POS_IMAGE_TTL = 60 * 60; // 1h — long enough for a session, short enough to matter

/** Full URLs written before 0069 are no longer servable; recover the path from them. */
function posImagePath(pathOrUrl) {
  if (!pathOrUrl) return null;
  const s = String(pathOrUrl);
  if (!s.startsWith("http")) return s;
  const marker = `/${POS_IMAGE_BUCKET}/`;
  const i = s.indexOf(marker);
  return i >= 0 ? s.slice(i + marker.length).split("?")[0] : null;
}

/**
 * Upload a POS item image to the account-scoped bucket.
 * Returns the storage PATH to persist — not a URL. Callers render it through
 * signPOSItemImages() / signPOSItemImage().
 */
export async function uploadPOSItemImage(itemKey, file) {
  const acct = await activeAccountId();
  const ext = (file?.name?.split(".").pop() || "png").toLowerCase();
  const path = `${acct}/${itemKey}.${ext}`;
  const { error: upErr } = await supabase.storage
    .from(POS_IMAGE_BUCKET)
    .upload(path, file, { upsert: true, contentType: file?.type });
  if (upErr) throw new Error(upErr.message);
  return path;
}

/** Signed URL for one stored path (or a legacy full URL). Null if unreadable. */
export async function signPOSItemImage(pathOrUrl) {
  const path = posImagePath(pathOrUrl);
  if (!path) return null;
  const { data, error } = await supabase.storage
    .from(POS_IMAGE_BUCKET).createSignedUrl(path, POS_IMAGE_TTL);
  if (error) { console.error("[referenceDataService.signPOSItemImage]", error); return null; }
  return data?.signedUrl ?? null;
}

/**
 * Batch version for grids — one round trip instead of one per row.
 * Takes pos_items rows, returns them with `image_src` set to a signed URL
 * (null when there's no image or the caller can't read it). Never throws: a
 * missing picture must not take out the item list.
 */
export async function signPOSItemImages(items) {
  const rows = items ?? [];
  const paths = [...new Set(rows.map((i) => posImagePath(i?.image_url)).filter(Boolean))];
  if (paths.length === 0) return rows.map((i) => ({ ...i, image_src: null }));

  const byPath = new Map();
  try {
    const { data, error } = await supabase.storage
      .from(POS_IMAGE_BUCKET).createSignedUrls(paths, POS_IMAGE_TTL);
    if (error) console.error("[referenceDataService.signPOSItemImages]", error);
    for (const d of data ?? []) if (d?.path && d?.signedUrl) byPath.set(d.path, d.signedUrl);
  } catch (e) {
    console.error("[referenceDataService.signPOSItemImages]", e);
  }
  return rows.map((i) => ({ ...i, image_src: byPath.get(posImagePath(i?.image_url)) ?? null }));
}

// ── POS Delivery Contacts (per-state/distributor warehouse address book) ─────
const CONTACT_COL_MAP = {
  state: "state",
  distributor: "distributor",
  warehouseName: "warehouse_name",
  posContactName: "pos_contact_name",
  posContactPhone: "pos_contact_phone",
  posContactEmail: "pos_contact_email",
  posStreetAddress: "pos_street_address",
  posCity: "pos_city",
  posZipcode: "pos_zipcode",
};

export async function loadPOSDeliveryContacts() {
  const acct = await activeAccountId();
  const { data, error } = await supabase
    .from("pos_delivery_contacts")
    .select("*")
    .eq("account_id", acct)
    .order("state", { ascending: true })
    .order("distributor", { ascending: true });
  if (error) { console.error("[referenceDataService.loadPOSDeliveryContacts]", error); throw new Error("Could not load delivery contacts."); }
  return data ?? [];
}

export async function createPOSDeliveryContact(fields) {
  const acct = await activeAccountId();
  if (!fields?.state || !fields?.distributor) throw new Error("State and distributor are required.");
  const row = { account_id: acct, ...pick(fields, CONTACT_COL_MAP) };
  const { data, error } = await supabase.from("pos_delivery_contacts").insert(row).select("*").single();
  if (error) throw writeError("createPOSDeliveryContact", error);
  return data;
}

export async function updatePOSDeliveryContact(id, fields) {
  const acct = await activeAccountId();
  const patch = pick(fields, CONTACT_COL_MAP);
  const { error } = await supabase.from("pos_delivery_contacts").update(patch).eq("account_id", acct).eq("id", id);
  if (error) throw writeError("updatePOSDeliveryContact", error);
  return { id };
}

export async function deletePOSDeliveryContact(id) {
  const acct = await activeAccountId();
  const { error } = await supabase.from("pos_delivery_contacts").delete().eq("account_id", acct).eq("id", id);
  if (error) throw writeError("deletePOSDeliveryContact", error);
  return { id };
}

// ── Product Samples catalog ──────────────────────────────────────────────────
export async function loadProductSamples() {
  const acct = await activeAccountId();
  const { data, error } = await supabase
    .from("product_samples")
    .select("*")
    .eq("account_id", acct)
    .order("sort_order", { ascending: true })
    .order("name", { ascending: true });
  if (error) { console.error("[referenceDataService.loadProductSamples]", error); throw new Error("Could not load product samples."); }
  return data ?? [];
}

// Same account-scoped list (kept for the ported admin component's API).
export async function loadAllProductSamplesAdmin() {
  return loadProductSamples();
}

export async function createProductSample({ name, description, sortOrder } = {}) {
  const acct = await activeAccountId();
  const n = String(name ?? "").trim();
  if (!n) throw new Error("Sample name is required.");
  const { data, error } = await supabase
    .from("product_samples")
    .insert({
      account_id: acct,
      name: n,
      description: description ? String(description).trim() : null,
      sort_order: Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 0,
    })
    .select("*")
    .single();
  if (error) throw writeError("createProductSample", error);
  return data;
}

export async function updateProductSample(id, { name, description } = {}) {
  const acct = await activeAccountId();
  const patch = {};
  if (name !== undefined) patch.name = String(name ?? "").trim();
  if (description !== undefined) patch.description = description ? String(description).trim() : null;
  const { error } = await supabase.from("product_samples").update(patch).eq("account_id", acct).eq("id", id);
  if (error) throw writeError("updateProductSample", error);
  return { id };
}

export async function deleteProductSample(id) {
  const acct = await activeAccountId();
  const { error } = await supabase.from("product_samples").delete().eq("account_id", acct).eq("id", id);
  if (error) throw writeError("deleteProductSample", error);
  return { id };
}
